/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  DataPortSchemaObject,
  FromSchema,
  JsonSchema,
  TypedArraySchemaOptions,
} from "@workglow/util/schema";
import { BaseTabularStorage, ClientProvidedKeysOption } from "./BaseTabularStorage";
import { assertCursorMatches, decodeCursor } from "./Cursor";
import {
  AutoGeneratedKeys,
  InsertEntity,
  OrderBy,
  Page,
  PageRequest,
  SearchCriteria,
  SimplifyPrimaryKey,
  ValueOptionType,
} from "./ITabularStorage";

/**
 * Base class for SQL-based tabular repositories, shared by SQLite and PostgreSQL
 * implementations.
 */
export abstract class BaseSqlTabularStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  // computed types
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  Value = Omit<Entity, PrimaryKeyNames[number] & keyof Entity>,
  InsertType extends InsertEntity<Entity, AutoGeneratedKeys<Schema>> = InsertEntity<
    Entity,
    AutoGeneratedKeys<Schema>
  >,
> extends BaseTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, Value, InsertType> {
  /** Caches for computed column definition strings, keyed by quote delimiter. */
  private readonly _pkColsCache = new Map<string, string>();
  private readonly _valColsCache = new Map<string, string>();
  private readonly _pkColListCache = new Map<string, string>();
  private readonly _valColListCache = new Map<string, string>();

  constructor(
    protected readonly table: string = "tabular_store",
    schema: Schema,
    primaryKeyNames: PrimaryKeyNames,
    indexes: readonly (keyof NoInfer<Entity> | readonly (keyof NoInfer<Entity>)[])[] = [],
    clientProvidedKeys: ClientProvidedKeysOption = "if-missing",
    tabularMigrations?: ReadonlyArray<import("../migrations").ITabularMigration>,
    migrationName?: string,
    uniqueIndexes: readonly (readonly (keyof NoInfer<Entity>)[])[] = []
  ) {
    super(
      schema,
      primaryKeyNames,
      indexes,
      clientProvidedKeys,
      tabularMigrations,
      migrationName ?? table,
      uniqueIndexes
    );
    this.validateTableAndSchema();
  }

  /** Maps a JSON Schema type to its dialect-specific SQL type. */
  protected abstract mapTypeToSQL(typeDef: JsonSchema): string;

  protected constructPrimaryKeyColumns($delimiter: string = ""): string {
    let cached = this._pkColsCache.get($delimiter);
    if (cached === undefined) {
      cached = Object.entries<JsonSchema>(this.primaryKeySchema.properties)
        .map(([key, typeDef]) => {
          const sqlType = this.mapTypeToSQL(typeDef);
          return `${$delimiter}${key}${$delimiter} ${sqlType} NOT NULL`;
        })
        .join(", ");
      this._pkColsCache.set($delimiter, cached);
    }
    return cached;
  }

  protected constructValueColumns($delimiter: string = ""): string {
    let cached = this._valColsCache.get($delimiter);
    if (cached === undefined) {
      const requiredSet = new Set(this.valueSchema.required ?? []);
      const cols = Object.entries<JsonSchema>(this.valueSchema.properties)
        .map(([key, typeDef]) => {
          const sqlType = this.mapTypeToSQL(typeDef);
          const isRequired = requiredSet.has(key);
          const nullable = !isRequired || this.isNullable(typeDef);
          return `${$delimiter}${key}${$delimiter} ${sqlType}${nullable ? " NULL" : " NOT NULL"}`;
        })
        .join(", ");
      cached = cols.length > 0 ? `, ${cols}` : "";
      this._valColsCache.set($delimiter, cached);
    }
    return cached;
  }

  protected isNullable(typeDef: JsonSchema): boolean {
    if (typeof typeDef === "boolean") return typeDef;

    if (typeDef.type === "null") {
      return true;
    }

    if (Array.isArray(typeDef.type)) {
      return typeDef.type.includes("null");
    }

    if (typeDef.anyOf && Array.isArray(typeDef.anyOf)) {
      return typeDef.anyOf.some((type: any) => type.type === "null");
    }

    if (typeDef.oneOf && Array.isArray(typeDef.oneOf)) {
      return typeDef.oneOf.some((type: any) => type.type === "null");
    }

    return false;
  }

  protected primaryKeyColumnList($delimiter: string = ""): string {
    let cached = this._pkColListCache.get($delimiter);
    if (cached === undefined) {
      cached =
        $delimiter + this.primaryKeyColumns().join(`${$delimiter}, ${$delimiter}`) + $delimiter;
      this._pkColListCache.set($delimiter, cached);
    }
    return cached;
  }

  protected valueColumnList($delimiter: string = ""): string {
    let cached = this._valColListCache.get($delimiter);
    if (cached === undefined) {
      cached = $delimiter + this.valueColumns().join(`${$delimiter}, ${$delimiter}`) + $delimiter;
      this._valColListCache.set($delimiter, cached);
    }
    return cached;
  }

  /** Extracts the non-null branch from a `T | null` union schema. */
  protected getNonNullType(typeDef: JsonSchema): JsonSchema {
    if (typeof typeDef === "boolean") return typeDef;

    if (typeDef.anyOf && Array.isArray(typeDef.anyOf)) {
      const nonNullType = typeDef.anyOf.find((t: any) => t.type !== "null");
      if (nonNullType) {
        return nonNullType;
      }
    }
    if (typeDef.oneOf && Array.isArray(typeDef.oneOf)) {
      const nonNullType = typeDef.oneOf.find((t: any) => t.type !== "null");
      if (nonNullType) {
        return nonNullType;
      }
    }
    return typeDef;
  }

  /**
   * Determines if a numeric field should be treated as unsigned, i.e. it is a
   * `number`/`integer` schema with a `minimum` of zero or greater. Shared by
   * the Postgres-shaped backends when selecting integer range types.
   */
  protected shouldBeUnsigned(typeDef: JsonSchema): boolean {
    const actualType = this.getNonNullType(typeDef);
    if (typeof actualType === "boolean") {
      return false;
    }
    return (
      (actualType.type === "number" || actualType.type === "integer") &&
      typeof actualType.minimum === "number" &&
      actualType.minimum >= 0
    );
  }

  /** Returns value fields ordered to match the schema declaration. */
  protected getValueAsOrderedArray(value: Value): ValueOptionType[] {
    const orderedParams: ValueOptionType[] = [];
    const valueAsRecord = value as Record<string, Entity[keyof Entity]>;
    const requiredSet = new Set(this.valueSchema.required ?? []);
    for (const key in this.valueSchema.properties) {
      if (Object.prototype.hasOwnProperty.call(valueAsRecord, key)) {
        const val = valueAsRecord[key];
        if (val === undefined && !requiredSet.has(key)) {
          orderedParams.push(null);
        } else {
          orderedParams.push(this.jsToSqlValue(key, val));
        }
      } else {
        if (requiredSet.has(key)) {
          throw new Error(`Missing required value field: ${key}`);
        }
        orderedParams.push(null);
      }
    }
    return orderedParams;
  }

  protected override getPrimaryKeyAsOrderedArray(key: PrimaryKey): ValueOptionType[] {
    const orderedParams: ValueOptionType[] = [];
    const keyObj = key as Record<string, Entity[keyof Entity]>;
    for (const k of Object.keys(this.primaryKeySchema.properties)) {
      if (k in keyObj) {
        const value = keyObj[k];
        if (value === null) {
          throw new Error(`Primary key field ${k} cannot be null`);
        }
        orderedParams.push(this.jsToSqlValue(k, value));
      } else {
        throw new Error(`Missing required primary key field: ${k}`);
      }
    }
    return orderedParams;
  }

  protected jsToSqlValue(column: string, value: Entity[keyof Entity]): ValueOptionType {
    const typeDef = this.schema.properties[column];
    if (!typeDef) {
      return value as ValueOptionType;
    }

    if (value === null && this.isNullable(typeDef)) {
      return null;
    }

    const actualType = this.getNonNullType(typeDef);
    if (typeof actualType === "boolean") {
      return value as ValueOptionType;
    }

    if (actualType.contentEncoding === "blob") {
      if (value instanceof Uint8Array) {
        return value;
      }
      if (typeof Buffer !== "undefined" && value instanceof Buffer) {
        return new Uint8Array(value);
      }
      if (Array.isArray(value)) {
        return new Uint8Array(value);
      }
      return value as ValueOptionType;
    } else if (value instanceof Date) {
      // Always coerce Date to ISO string regardless of declared column type.
      return value.toISOString();
    } else {
      return value as ValueOptionType;
    }
  }

  protected sqlToJsValue(column: string, value: ValueOptionType): Entity[keyof Entity] {
    const typeDef = this.schema.properties[column];
    if (!typeDef) {
      return value as Entity[keyof Entity];
    }

    if (value === null && this.isNullable(typeDef)) {
      return null as Entity[keyof Entity];
    }

    const actualType = this.getNonNullType(typeDef);
    if (typeof actualType === "boolean") {
      return value as Entity[keyof Entity];
    }

    if (actualType.contentEncoding === "blob") {
      if (typeof Buffer !== "undefined" && value instanceof Buffer) {
        return new Uint8Array(value) as Entity[keyof Entity];
      }
      if (value instanceof Uint8Array) {
        return value as Entity[keyof Entity];
      }
      return value as Entity[keyof Entity];
    } else {
      return value as Entity[keyof Entity];
    }
  }

  /**
   * Rejects table names and schema keys that cannot be safely interpolated into
   * DDL, and rejects schemas where PK and value keys collide.
   */
  protected validateTableAndSchema(): void {
    if (!/^[a-z]\w*$/i.test(this.table)) {
      throw new Error(
        "Table name must start with a letter and contain only letters, digits, and underscores, got: " +
          this.table
      );
    }

    const validateSchemaKeys = (schema: DataPortSchemaObject) => {
      for (const key in schema.properties) {
        if (!/^[a-z]\w*$/i.test(key)) {
          throw new Error(
            "Schema keys must start with a letter and contain only letters, digits, and underscores, got: " +
              key
          );
        }
      }
    };

    validateSchemaKeys(this.primaryKeySchema);
    validateSchemaKeys(this.valueSchema);

    const primaryKeys = new Set(Object.keys(this.primaryKeySchema.properties));
    const valueKeys = Object.keys(this.valueSchema.properties);
    const duplicates = valueKeys.filter((key) => primaryKeys.has(key));
    if (duplicates.length > 0) {
      throw new Error(`Duplicate keys found in schemas: ${duplicates.join(", ")}`);
    }
  }

  /**
   * Builds a keyset (seek) WHERE clause that selects rows strictly after
   * the cursor position according to the provided ordering. Handles mixed
   * ASC/DESC by expanding into the OR-of-AND form:
   *
   *   (col1 OP1 v1)
   *   OR (col1 = v1 AND col2 OP2 v2)
   *   OR (col1 = v1 AND col2 = v2 AND col3 OP3 v3)
   *   ...
   *
   * NULL handling. We adopt the same NULL ordering as the in-memory
   * comparator: NULLs sort *before* non-null values for ASC, *after* them
   * for DESC. The pair `runSqlPage` / `buildKeysetWhere` enforce this with
   * explicit `NULLS FIRST` / `NULLS LAST` in the ORDER BY plus NULL-aware
   * predicates here. Because the cursor's values are known at SQL-build
   * time, we branch at build time on NULL vs non-NULL rather than relying
   * on dialect-specific operators like `IS NOT DISTINCT FROM`:
   *
   *   - Equality on a preceding column:
   *       NULL  -> `col IS NULL`
   *       value -> `col = ?`
   *   - Strict comparison on the i-th column:
   *       ASC, NULL  -> `col IS NOT NULL`              (NULL is the smallest)
   *       ASC, value -> `col > ?`                       (NULLs sort before, excluded)
   *       DESC, NULL -> `1 = 0`                         (nothing comes after a NULL trailer)
   *       DESC, value-> `(col < ? OR col IS NULL)`      (smaller, then NULLs)
   *
   * @param effectiveOrderBy - Ordering to satisfy; must include the primary
   *   key columns at the tail so the comparison is total.
   * @param cursorValues - Values pulled from the cursor in the same order
   *   as `effectiveOrderBy`.
   * @param quote - Identifier quote character (e.g. `` ` `` or `"`).
   * @param placeholder - Function returning the placeholder string for the
   *   given 1-based parameter index.
   * @param startIndex - Param index to start numbering from. Returns the
   *   index to use after this clause's params.
   */
  protected buildKeysetWhere(
    effectiveOrderBy: ReadonlyArray<OrderBy<Entity>>,
    cursorValues: ReadonlyArray<ValueOptionType>,
    quote: string,
    placeholder: (index: number) => string,
    startIndex: number
  ): { whereClause: string; params: ValueOptionType[]; nextIndex: number } {
    if (effectiveOrderBy.length !== cursorValues.length) {
      throw new Error(
        `Keyset arity mismatch: ${effectiveOrderBy.length} order columns vs ${cursorValues.length} cursor values`
      );
    }
    const clauses: string[] = [];
    const params: ValueOptionType[] = [];
    let idx = startIndex;
    for (let i = 0; i < effectiveOrderBy.length; i++) {
      const parts: string[] = [];
      // Equality on all preceding columns. NULL-aware: collapse to IS NULL
      // when the cursor value is null, since SQL `col = NULL` is never true.
      for (let j = 0; j < i; j++) {
        const colExpr = `${quote}${String(effectiveOrderBy[j].column)}${quote}`;
        if (cursorValues[j] === null) {
          parts.push(`${colExpr} IS NULL`);
        } else {
          parts.push(`${colExpr} = ${placeholder(idx)}`);
          params.push(cursorValues[j]);
          idx++;
        }
      }
      // Strict comparison on the i-th column in its declared direction.
      const colExpr = `${quote}${String(effectiveOrderBy[i].column)}${quote}`;
      const v = cursorValues[i];
      const dir = effectiveOrderBy[i].direction;
      if (v === null) {
        // ASC NULLS FIRST: anything non-null comes after a NULL cursor.
        // DESC NULLS LAST: NULL is the trailing run; nothing comes after.
        parts.push(dir === "ASC" ? `${colExpr} IS NOT NULL` : `1 = 0`);
      } else {
        if (dir === "ASC") {
          parts.push(`${colExpr} > ${placeholder(idx)}`);
        } else {
          // DESC: rows smaller than v, then any NULLs after them.
          parts.push(`(${colExpr} < ${placeholder(idx)} OR ${colExpr} IS NULL)`);
        }
        params.push(v);
        idx++;
      }
      clauses.push(`(${parts.join(" AND ")})`);
    }
    return { whereClause: clauses.join(" OR "), params, nextIndex: idx };
  }

  /**
   * Shared SQL `getPage`/`queryPage` driver. Builds a single SELECT that
   * pushes the keyset predicate, ordering, and limit down to the database
   * so memory and wire traffic are O(pageSize) regardless of table size.
   *
   * Concrete SQL backends call this from their `getPage`/`queryPage`
   * overrides, supplying the dialect-specific quoting, placeholder
   * formatting, search-criteria WHERE builder, and select executor.
   */
  protected async runSqlPage(
    criteria: SearchCriteria<Entity> | undefined,
    request: PageRequest<Entity>,
    dialect: {
      readonly quote: string;
      readonly placeholder: (index: number) => string;
      readonly buildSearchWhere: (
        criteria: SearchCriteria<Entity>,
        startIndex: number
      ) => { whereClause: string; params: ValueOptionType[]; nextIndex: number };
      readonly executeSelect: (sql: string, params: ValueOptionType[]) => Promise<Entity[]>;
    }
  ): Promise<Page<Entity>> {
    // Validate before we interpolate any column names into SQL — the caller
    // is the trust boundary for orderBy contents.
    this.validatePageRequest(request);
    const limit = request.limit ?? 100;
    const pkColumns = this.primaryKeyColumns() as unknown as Array<keyof Entity>;
    const orderBy = request.orderBy;
    const effectiveOrderBy = this.buildEffectiveOrderBy(orderBy, pkColumns);
    const effectiveOrderForCursor = effectiveOrderBy.map((o) => ({
      column: String(o.column),
      direction: o.direction,
    }));

    let cursorPayload;
    if (request.cursor !== undefined) {
      cursorPayload = decodeCursor(request.cursor);
      assertCursorMatches(cursorPayload, effectiveOrderForCursor);
    }

    const params: ValueOptionType[] = [];
    let paramIdx = 1;
    const whereClauses: string[] = [];

    if (criteria && Object.keys(criteria).length > 0) {
      const built = dialect.buildSearchWhere(criteria, paramIdx);
      whereClauses.push(built.whereClause);
      params.push(...built.params);
      paramIdx = built.nextIndex;
    }

    if (cursorPayload) {
      // Re-encode cursor values through `jsToSqlValue` so dates and other
      // non-primitive types round-trip through the SQL driver the same way
      // the original row values did.
      const cursorValues = (cursorPayload.c as ValueOptionType[]).map((v, i) =>
        this.jsToSqlValue(String(effectiveOrderBy[i].column), v as Entity[keyof Entity])
      );
      const built = this.buildKeysetWhere(
        effectiveOrderBy,
        cursorValues,
        dialect.quote,
        dialect.placeholder,
        paramIdx
      );
      whereClauses.push(`(${built.whereClause})`);
      params.push(...built.params);
      paramIdx = built.nextIndex;
    }

    const q = dialect.quote;
    // Emit NULLS FIRST / NULLS LAST explicitly because SQLite (NULLS first
    // for ASC) and Postgres (NULLS last for ASC) disagree by default. The
    // keyset predicates in `buildKeysetWhere` assume NULLs-first-for-ASC,
    // NULLs-last-for-DESC; the ORDER BY must match that for resumed pages
    // to land in the right slot.
    const orderByClause = effectiveOrderBy
      .map((o) => {
        const nulls = o.direction === "ASC" ? "NULLS FIRST" : "NULLS LAST";
        return `${q}${String(o.column)}${q} ${o.direction} ${nulls}`;
      })
      .join(", ");

    let sql = `SELECT * FROM ${q}${this.table}${q}`;
    if (whereClauses.length > 0) {
      sql += ` WHERE ${whereClauses.join(" AND ")}`;
    }
    sql += ` ORDER BY ${orderByClause}`;
    sql += ` LIMIT ${dialect.placeholder(paramIdx)}`;
    params.push(limit);

    const items = await dialect.executeSelect(sql, params);

    const nextCursor =
      items.length === limit
        ? this.buildCursor(items[items.length - 1], effectiveOrderBy)
        : undefined;

    return { items, nextCursor };
  }
}
